import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { filterNil } from '@ngneat/elf';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AccessControl } from '@pncl/common-models';

import {
  ActionsQueryParamKeys,
  checkIfQueryParamSetToTrue,
} from 'src/app/common/utils/actions-query-params-util';
import { ISession } from 'src/app/models/session';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { UserService } from 'src/app/services/user.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import { CapabilityTierService } from 'src/app/services/capability-tier.service';
import { FLAGS, FlagsService } from './flags.service';
import { ModalManagerService } from './modal-manager.service';
import { isObserverTabId, ObserverModeSuffix } from './observer-mode.utils';
import { TelemetryService } from './telemetry.service';
import Capability = AccessControl.Capability;

@Injectable({
  providedIn: 'root',
})
export class ObserverModeService {
  constructor(
    private readonly flagsService: FlagsService,
    private readonly spaceRepository: SpaceRepository,
    private readonly userService: UserService,
    private readonly modalManagerService: ModalManagerService,
    private readonly translateService: TranslateService,
    private readonly locationService: Location,
    private readonly telemetry: TelemetryService,
    private readonly capabilityTierService: CapabilityTierService,
  ) {
    // When active space id clears (leave, switch space, reset), presence removes synchronously
    // in PresenceProvider's combineLatest. Defer suffix removal to a microtask so it runs after
    // that synchronous Elf/store notification fan-out completes.
    this.spaceRepository.activeSpaceId$
      .pipe(
        pairwise(),
        filter(([prev, curr]) => !!prev && !curr),
      )
      .subscribe(() => {
        queueMicrotask(() => this.handleLeaveSpaceIfObserverModeActive());
      });
  }

  observerModeEnabled$: Observable<boolean> = combineLatest([
    this.flagsService.featureFlagChanged(FLAGS.SPACES_OBSERVER_MODE),
    this.capabilityTierService.shouldEnableCapability$(Capability.OBSERVER_MODE),
  ]).pipe(map(([isFlagEnabled, isCapabilityEnabled]) => isFlagEnabled && isCapabilityEnabled));

  observerModeEnabledForInstitution$: Observable<boolean> = combineLatest([
    this.flagsService.featureFlagChanged(FLAGS.SPACES_OBSERVER_MODE),
    this.userService.user.pipe(filterNil()),
    this.capabilityTierService.shouldEnableCapability$(Capability.OBSERVER_MODE),
  ]).pipe(
    map(
      ([isFlagEnabled, , isCapabilityEnabled]) =>
        isFlagEnabled && isCapabilityEnabled && this.isObserverModeEnabledForCurrentInstitution(),
    ),
  );

  promptEnterSpaceAsObserver$(): Observable<boolean> {
    return this.modalManagerService
      .showConfirmationModal({
        title: this.translateService.instant('Enter as observer'),
        description: this.translateService.instant(
          'You will enter the Space invisibly. Your name and presence will be hidden from others; admins can still see your presence.',
        ),
        showBodyImg: true,
        bodyImg: {
          url: 'assets/imgs/observer-entry-modal-icon.png',
          width: '320px',
          height: '240px',
          alt: this.translateService.instant('Enter as observer'),
        },
        confirmButtonConfig: {
          icon: 'arrow_forward',
          iconType: IconTypes.matIcon,
          label: this.translateService.instant('Enter'),
        },
        descriptionGap: false,
      })
      .pipe(take(1));
  }

  observerModeActive$: Observable<boolean> = this.spaceRepository.activeSpaceId$.pipe(
    distinctUntilChanged(),
    map((id) => (id ? this.isObserverModeActive() : false)),
  );

  isObserverModeEnabledForSpace(space?: ISession): boolean {
    if (!space || !this.flagsService.isFlagEnabled(FLAGS.SPACES_OBSERVER_MODE)) {
      return false;
    }

    const currentUser = this.userService.user.getValue()?.user;

    if (!currentUser || !space) {
      return false;
    }

    return (
      this.isObserverModeEnabledForCurrentInstitution() &&
      this.spaceRepository.hasSpaceAdminPermissions(currentUser, space, true)
    );
  }

  isObserverModeEnabledForCurrentInstitution(): boolean {
    const currentUser = this.userService.user.getValue()?.user;
    const isCapabilityEnabled = this.capabilityTierService.shouldEnableCapability(
      Capability.OBSERVER_MODE,
    );

    return (
      isCapabilityEnabled && !!currentUser?.institution?.settings?.allowAdminsAsObserversInSpace
    );
  }

  handleEnterSpaceAsObserver(suffix: ObserverModeSuffix): void {
    this.telemetry.logBasicEvent('[observer_flow]', {
      action: 'enter_space_as_observer',
    });
    this.userService.appendSuffixToUserUniqueHash(suffix);
    this.setObserverModeQueryParamInUrl();
  }

  handleLeaveSpaceIfObserverModeActive(): void {
    const observerModeActive = this.isObserverModeActive();
    this.telemetry.logBasicEvent('[observer_flow]', {
      action: 'leave_space',
      observerModeActive,
    });

    if (observerModeActive) {
      this.userService.removeSuffixFromUserUniqueHash(ObserverModeSuffix.OBSERVER);
    }
    this.removeObserverModeQueryParamFromUrl();
  }

  isObserverModeActive() {
    const userTabHash = this.userService.userUniqueHash;
    return isObserverTabId(userTabHash);
  }

  removeObserverModeQueryParamFromUrl(): void {
    this.updateObserverModeQueryParamInUrl(false);
    this.telemetry.logBasicEvent('[observer_flow]', {
      action: 'remove_query_param',
    });
  }

  private setObserverModeQueryParamInUrl(): void {
    // add the query param after the active space is set
    // so the query param is not removed by entering space navigations
    this.spaceRepository.activeSpaceId$.pipe(filterNil(), take(1)).subscribe(() => {
      this.updateObserverModeQueryParamInUrl(true);
      this.telemetry.logBasicEvent('[observer_flow]', {
        action: 'set_query_param',
      });
    });
  }

  private updateObserverModeQueryParamInUrl(shouldSetParam: boolean): void {
    const newQueryParams = new URLSearchParams(location.search);

    if (shouldSetParam) {
      newQueryParams.set(ActionsQueryParamKeys.OBSERVER_MODE, 'true');
    } else {
      newQueryParams.delete(ActionsQueryParamKeys.OBSERVER_MODE);
    }

    const currentState = this.locationService.getState();
    this.locationService.replaceState(location.pathname, newQueryParams.toString(), currentState);
  }

  handleObserverModeFromQueryParamFlow(space: ISession) {
    this.telemetry.logBasicEvent('[observer_flow]', {
      action: 'handle_query_param_flow',
      spaceId: space?._id,
    });

    if (this.shouldEnableObserverModeFromQueryParamFlow(space)) {
      this.handleEnterSpaceAsObserver(ObserverModeSuffix.OBSERVER);
    } else {
      this.removeObserverModeQueryParamFromUrl();
    }
  }

  private shouldEnableObserverModeFromQueryParamFlow(space: ISession): boolean {
    if (!space || !checkIfQueryParamSetToTrue(ActionsQueryParamKeys.OBSERVER_MODE)) {
      return false;
    }

    return this.isObserverModeEnabledForSpace(space);
  }
}
